import { Injectable } from '@angular/core';
import { Action, State, StateContext, StateToken } from '@ngxs/store';
// import jwtDecode from 'jwt-decode';
import { throwError } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { AccountService } from '../services/account.service';
import { CORE_FUNCTIONS } from '../utils';
import { AppStoreActions } from './app.store.actions';
import { AppStoreModel, IAppSession, initialState } from './app.store.model';

export const APP_STATE_TOKEN = new StateToken<AppStoreModel>('appState');

@State({
    name: APP_STATE_TOKEN,
    defaults: initialState
})
@Injectable()
export class AppState {
    // ===================================================
    // CONFIG
    // ===================================================

    constructor(private accountService: AccountService) {

    }

    @Action(AppStoreActions.Config.ChangeBreadCrumb)
    setBreadCrumb(
        { getState, setState }: StateContext<AppStoreModel>,
        { payload }: AppStoreActions.Config.ChangeBreadCrumb
    ) {
        const state = getState();
        setState({
            ...state,
            globalConfig: {
                ...state.globalConfig,
                appConfig: {
                    ...state.globalConfig.appConfig,
                    breadcrumb: payload
                }
            }
        });
    }

    @Action(AppStoreActions.Config.AgregarUniversidadComparador)
    AgregarUniversidadComparador(
        { getState, setState }: StateContext<AppStoreModel>,
        { payload }: AppStoreActions.Config.AgregarUniversidadComparador
    ) {
        const state = getState();
        const newState = setState({
            ...state,
            globalConfig: {
                ...state.globalConfig,
                comparador: {
                    ...state.globalConfig.comparador,
                    universidades: [
                        ...state.globalConfig.comparador.universidades,
                        ...payload
                    ]
                }
            }
        });
    }

    @Action(AppStoreActions.Config.QuitarUniversidadComparador)
    QuitarUniversidadComparador(
        { getState, setState }: StateContext<AppStoreModel>,
        { payload }: AppStoreActions.Config.QuitarUniversidadComparador
    ) {
        const state = getState();
        setState({
            ...state,
            globalConfig: {
                ...state.globalConfig,
                comparador: {
                    ...state.globalConfig.comparador,
                    universidades: state.globalConfig.comparador.universidades.filter(i => i.id != payload.id)
                }
            }
        });
    }

    @Action(AppStoreActions.Config.AgregarProgramaComparador)
    AgregarProgramaComparador(
        { getState, setState }: StateContext<AppStoreModel>,
        { payload }: AppStoreActions.Config.AgregarProgramaComparador
    ) {
        const state = getState();
        const newState = setState({
            ...state,
            globalConfig: {
                ...state.globalConfig,
                comparador: {
                    ...state.globalConfig.comparador,
                    programas: [
                        ...state.globalConfig.comparador.programas,
                        ...payload
                    ]
                }
            }
        });
    }

    @Action(AppStoreActions.Config.QuitarProgramaComparador)
    QuitarProgramaComparador(
        { getState, setState }: StateContext<AppStoreModel>,
        { payload }: AppStoreActions.Config.QuitarProgramaComparador
    ) {
        const state = getState();
        setState({
            ...state,
            globalConfig: {
                ...state.globalConfig,
                comparador: {
                    ...state.globalConfig.comparador,
                    programas: state.globalConfig.comparador.programas.filter(i => !(i.idPrograma == payload.id && i.idUniversidad == payload.idUniversidad && i.idModalidadEstudio == payload.idModalidadEstudio))
                }
            }
        });
    }

    @Action(AppStoreActions.Config.SetUserGeolocation)
    SetUsergeolocation(
        { getState, setState }: StateContext<AppStoreModel>,
        { payload }: AppStoreActions.Config.SetUserGeolocation
    ) {
        const state = getState();
        setState({
            ...state,
            globalConfig: {
                ...state.globalConfig,
                userGeoLocation: payload.userGeoLocation
            }
        });
    }

    // ===================================================
    // UI
    // ===================================================

    @Action(AppStoreActions.Ui.HideSidebar)
    hideSidebar(
        { getState, setState }: StateContext<AppStoreModel>,
        { payload: hideSidebar }: AppStoreActions.Ui.HideSidebar
    ) {
        const state = getState();
        setState({
            ...state,
            ui: {
                ...state.ui,
                hideSidebar
            }
        });
    }

    @Action(AppStoreActions.Ui.SetMenu)
    setMenu(
        { getState, patchState }: StateContext<AppStoreModel>,
        { payload: { rawMenu } }: AppStoreActions.Ui.SetMenu
    ) {
        const state = getState();

        patchState({
            ui: {
                ...state.ui,
                menu: CORE_FUNCTIONS.buildMenu(rawMenu)
            }
        });
    }

    @Action(AppStoreActions.Ui.SetMobileLayout)
    SetMobileLayout(
        { getState, patchState }: StateContext<AppStoreModel>,
        { payload: { isMobile } }: AppStoreActions.Ui.SetMobileLayout
    ) {
        const state = getState();

        patchState({
            ui: {
                ...state.ui,
                mobileLayout: isMobile
            }
        });
    }

    @Action(AppStoreActions.Ui.OpenComparadorInferior)
    OpenComparadorInferior(
        { getState, patchState }: StateContext<AppStoreModel>,
        { payload: { open } }: AppStoreActions.Ui.OpenComparadorInferior
    ) {
        const state = getState();

        patchState({
            ui: {
                ...state.ui,
                comparadorInferior: {
                    ...state.ui.comparadorInferior,
                    open
                }
            }
        });
    }

    @Action(AppStoreActions.Ui.ColapsarComparadorInferior)
    ColapsarComparadorInferior(
        { getState, patchState }: StateContext<AppStoreModel>,
        { payload: { collapse } }: AppStoreActions.Ui.ColapsarComparadorInferior
    ) {
        const state = getState();

        patchState({
            ui: {
                ...state.ui,
                comparadorInferior: {
                    ...state.ui.comparadorInferior,
                    collapsed: collapse
                }
            }
        });
    }

    @Action(AppStoreActions.Ui.SetStickyMenuElevation)
    SetStickyMenuElevation(
        { getState, patchState }: StateContext<AppStoreModel>,
        { payload: { elevation } }: AppStoreActions.Ui.SetStickyMenuElevation
    ) {
        const state = getState();

        patchState({
            ui: {
                ...state.ui,
                stickyMenuElevation: elevation
            }
        });
    }

    // ===================================================
    // SESSION
    // ===================================================

    @Action(AppStoreActions.Session.SetToken)
    setToken(
        { getState, patchState }: StateContext<AppStoreModel>,
        { payload: { token } }: AppStoreActions.Session.SetToken
    ) {
        // const state = getState();
        // const decodedUser = jwtDecode<IAppSession>(token);

        // patchState({
        //     session: {
        //         ...state.session,
        //         token,
        //         isLoggedIn: true,
        //         session: {
        //             ...state.session.session,
        //             person: decodedUser.person,
        //             personid: decodedUser.personid,
        //             userid: decodedUser.userid,
        //             username: decodedUser.username,
        //             cargoid: +decodedUser.cargoid,
        //             ccosto: decodedUser.ccosto,
        //             ccontable: decodedUser.ccontable,
        //             defaultRol: null,
        //             permisos: []
        //         }
        //     }
        // });
    }


    @Action(AppStoreActions.Session.SetSessionFromStorage)
    setSessionFromStorage(
        { getState, patchState }: StateContext<AppStoreModel>,
        { payload: { session } }: AppStoreActions.Session.SetSessionFromStorage
    ) {
        const state = getState();

        patchState({
            session: {
                ...session,
                fromStorage: true
            }
        });
    }

    @Action(AppStoreActions.Session.GetRawMenu)
    asyncGetRawMenu(
        { getState, patchState, setState }: StateContext<AppStoreModel>,
        { payload: { idPerfil } }: AppStoreActions.Session.GetRawMenu
    ) {

        return this.accountService.getMenu(idPerfil).pipe(
            tap(response => {
                const state = getState();
                setState({
                    ...state,
                    session: {
                        ...state.session,
                        rawMenu: response.data
                    }
                });
            }),
            catchError(err => {
                return throwError(err);
            })
        );
    }

    @Action(AppStoreActions.Session.Login)
    asyncLogin(
        { dispatch }: StateContext<AppStoreModel>,
        { payload }: AppStoreActions.Session.Login
    ) {
        return this.accountService.login(payload.credentials).pipe(
            tap(token => {
                return dispatch(new AppStoreActions.Session.SetToken({ token }));
            }),
            catchError(err => {
                return throwError(err);
            })
        );
    }

    @Action(AppStoreActions.Session.Logout)
    logout(
        { getState, patchState }: StateContext<AppStoreModel>,
        { }: AppStoreActions.Session.Logout
    ) {
        const state = getState();

        patchState({
            session: initialState.session
        });
    }

    @Action(AppStoreActions.Session.SetRol)
    SetRol(
        { getState, patchState }: StateContext<AppStoreModel>,
        { payload: { rol: defaultRol } }: AppStoreActions.Session.SetRol
    ) {
        const state = getState();

        patchState({
            session: {
                ...state.session,
                session: {
                    ...state.session.session,
                    defaultRol
                }
            }
        });
    }

    @Action(AppStoreActions.Session.GetRoles)
    asyncGetRoles(
        { getState, patchState, dispatch }: StateContext<AppStoreModel>,
        { }: AppStoreActions.Session.GetRoles
    ) {
        const state = getState();
        return this.accountService.getPerfil(state.session.session.userid).pipe(
            tap(roles => {
                patchState({
                    session: {
                        ...state.session,
                        roles,
                        session: {
                            ...state.session.session,
                            defaultRol: roles.length > 0 ? roles[0].idPerfil : null
                        }
                    }
                });
            }),
            mergeMap(() => dispatch(new AppStoreActions.Session.GetPermisos())),
            catchError(err => {
                return throwError(err);
            })
        );
    }

    @Action(AppStoreActions.Session.GetPermisos)
    asyncGetPermisos(
        { getState, patchState }: StateContext<AppStoreModel>,
        { }: AppStoreActions.Session.GetPermisos
    ) {
        const state = getState();
        return this.accountService.getPermisos(
            state.session.session.defaultRol
        ).pipe(
            tap(permisos => {
                patchState({
                    session: {
                        ...state.session,
                        session: {
                            ...state.session.session,
                            permisos
                        }
                    }
                });
            }),
            catchError(err => {
                return throwError(err);
            })
        );
    }

}